import {
  BanknoteIcon,
  CreditCardIcon,
  GiftIcon,
  LandmarkIcon,
  QrCodeIcon,
  ReceiptIcon,
  StarIcon,
  WalletIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { PaymentMethodNameEnum } from "@/enums/orders"
import { cn } from "@/lib/utils"

// Reutilizando a estrutura existente dos ícones
const PAYMENT_METHOD_ICONS: Record<
  PaymentMethodNameEnum,
  React.ElementType
> = {
  [PaymentMethodNameEnum.BALANCE]: WalletIcon,
  [PaymentMethodNameEnum.CREDIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.DEBIT_CARD]: CreditCardIcon,
  [PaymentMethodNameEnum.LOYALTY_POINTS]: StarIcon,
  [PaymentMethodNameEnum.GIFT_CARD_VTEX]: GiftIcon,
  [PaymentMethodNameEnum.OPEN_FINANCE]: LandmarkIcon,
  [PaymentMethodNameEnum.BANK_SLIP]: ReceiptIcon,
  [PaymentMethodNameEnum.BOLEPIX]: ReceiptIcon,
  [PaymentMethodNameEnum.DIRECT_DEBIT]: LandmarkIcon,
  [PaymentMethodNameEnum.NUPAY]: BanknoteIcon,
  [PaymentMethodNameEnum.PIX]: QrCodeIcon,
  [PaymentMethodNameEnum.PIX_AUTO]: QrCodeIcon,
  [PaymentMethodNameEnum.CREDIT_CARD_PRE_APPROVAL]:
    CreditCardIcon,
}

// Cores modernas estilo big tech
const PAYMENT_METHOD_COLORS: Record<
  PaymentMethodNameEnum,
  string
> = {
  [PaymentMethodNameEnum.BALANCE]:
    "bg-slate-50 text-slate-700 border-slate-100",
  [PaymentMethodNameEnum.CREDIT_CARD]:
    "bg-blue-50 text-blue-700 border-blue-100",
  [PaymentMethodNameEnum.DEBIT_CARD]:
    "bg-violet-50 text-violet-700 border-violet-100",
  [PaymentMethodNameEnum.LOYALTY_POINTS]:
    "bg-yellow-50 text-yellow-700 border-yellow-100",
  [PaymentMethodNameEnum.GIFT_CARD_VTEX]:
    "bg-pink-50 text-pink-700 border-pink-100",
  [PaymentMethodNameEnum.OPEN_FINANCE]:
    "bg-indigo-50 text-indigo-700 border-indigo-100",
  [PaymentMethodNameEnum.BANK_SLIP]:
    "bg-amber-50 text-amber-700 border-amber-100",
  [PaymentMethodNameEnum.BOLEPIX]:
    "bg-amber-50 text-amber-700 border-amber-100",
  [PaymentMethodNameEnum.DIRECT_DEBIT]:
    "bg-teal-50 text-teal-700 border-teal-100",
  [PaymentMethodNameEnum.NUPAY]:
    "bg-green-50 text-green-700 border-green-100",
  [PaymentMethodNameEnum.PIX]:
    "bg-emerald-50 text-emerald-700 border-emerald-100",
  [PaymentMethodNameEnum.PIX_AUTO]:
    "bg-emerald-50 text-emerald-700 border-emerald-100",
  [PaymentMethodNameEnum.CREDIT_CARD_PRE_APPROVAL]:
    "bg-blue-50 text-blue-700 border-blue-100",
}

type PaymentMethodBadgeProps = {
  paymentMethod?: PaymentMethodNameEnum
  className?: string
}

export const PaymentMethodBadge = ({
  paymentMethod,
  className,
}: PaymentMethodBadgeProps) => {
  const { t } = useTranslation()

  // Verificar se paymentMethod existe e é válido
  if (!paymentMethod) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium",
          "border-slate-200 bg-slate-50 text-slate-500",
          className
        )}
      >
        <CreditCardIcon className="h-5 w-5 lg:h-4 lg:w-4" />
        <span>—</span>
      </div>
    )
  }

  // Casting para PaymentMethodNameEnum para garantir o tipo correto
  const paymentMethodEnum =
    paymentMethod as PaymentMethodNameEnum

  // Reutilizando as traduções existentes do sistema
  const label = t(
    `common.paymentMethods.${paymentMethodEnum}`
  )
  const Icon =
    PAYMENT_METHOD_ICONS[paymentMethodEnum] ||
    CreditCardIcon
  const colorClass =
    PAYMENT_METHOD_COLORS[paymentMethodEnum] ||
    "bg-slate-50 text-slate-700 border-slate-200"

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors",
        colorClass,
        className
      )}
    >
      <Icon className="h-5 w-5 lg:h-4 lg:w-4" />
      <span>{label}</span>
    </div>
  )
}
